import { capitalizeFirstLetter } from "@dashboard/library";

import {
  BuildDataDogMessageArgs,
  FormatNumberWithThresholdOptions,
} from "./string.helpers.decl";

export function formatToPercentage(value: number) {
  return `${value}%`;
}

/*
 * Name-order helpers below are copied verbatim from @dashboard/library
 * (`MATCH_CJK_SCRIPT`, `isCjkName`, `getInitials`, `formatPersonName`). The
 * version of @dashboard/library with these helpers requires React 19, while
 * this app is on React 18, so they are mirrored here instead of imported. Keep
 * in sync with the library source.
 */
const MATCH_CJK_SCRIPT =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;

/**
 * Whether any of the given name parts contain CJK characters (Han, Hiragana,
 * Katakana or Hangul). CJK names conventionally read surname first, so this
 * drives name display order from the name itself, independently of the
 * viewer's locale.
 * @param parts - Name parts to inspect (e.g. first name, last name).
 * @returns Whether any part contains a CJK character.
 */
export function isCjkName(...parts: string[]): boolean {
  return MATCH_CJK_SCRIPT.test(parts.join(""));
}

/**
 * Get the initials of a username in display order.
 *
 * For CJK names the surname initial comes first (matching `formatPersonName`
 * surname-first ordering); for all other scripts the given-name initial comes
 * first.
 * @param firstName - The given name.
 * @param lastName - The family name.
 * @returns The initials in display order, uppercased.
 */
export function getInitials(firstName: string, lastName = ""): string {
  const first = firstName.trim();
  const last = lastName.trim();
  return isCjkName(first, last)
    ? (last.charAt(0) + first.charAt(0)).toUpperCase()
    : (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export type PersonNameOrder = "given-first" | "surname-first";

export interface FormatPersonNameOptions {
  /**
   * Force a name order. When omitted, the order is detected from the name's
   * script: CJK names read surname first, everything else given first.
   */
  order?: PersonNameOrder;
  lastName: string;
}

/**
 * Format a person's name in the locale-correct order.
 *
 * Given-first cultures render "First Last"; surname-first cultures render
 * "Last First", joined by a single space. CJK names are detected from their
 * script and ordered surname-first automatically; for surname-first locales
 * written in Latin script (e.g. Hungarian) pass an explicit `order`. Casing is
 * left untouched - ordering is the only concern here.
 * @param firstName - The given name.
 * @param lastName - The family name.
 * @param options - Optional explicit order override.
 * @returns The ordered full name, or the single non-empty part when one is missing.
 */
export function formatPersonName(
  firstName: string,
  { lastName, order }: FormatPersonNameOptions & { lastName: string }
): string {
  const first = firstName.trim();
  const last = lastName.trim();

  if (!first || !last) {
    return first || last;
  }

  const resolvedOrder =
    order ?? (isCjkName(first, last) ? "surname-first" : "given-first");

  return resolvedOrder === "surname-first"
    ? `${last} ${first}`
    : `${first} ${last}`;
}

export function buildDataDogMessage({
  message,
  queryName,
}: BuildDataDogMessageArgs) {
  if (!queryName) {
    return `useQuery error: "${message}".`;
  }

  return `useQuery error: "${message}" on "${queryName}".`;
}

export function formatNumberWithThreshold(
  count: number,
  { threshold }: FormatNumberWithThresholdOptions
): string {
  if (count > threshold) {
    return `${threshold}+`;
  }

  return count.toString();
}

export function trimWithEllipsis(str: string, threshold: number) {
  if (str.length <= threshold) {
    return str;
  }

  return `${str.slice(0, threshold)}...`;
}

/**
 * Will capitalize a string
 * @param fullName - First and last name to capitalize
 * @returns - Capitalized string
 *
 * ## Example
 * ```typescript
 * capitalizeFullName('samwise gamgee') // => 'Samwise Gamgee'
 * capitalizeFullName('sam') // => 'Sam'
 * capitalizeFullName('gandalf the grey') // => 'Gandalf The Grey'
 * ```
 */
export function capitalizeString(fullName: string): string {
  const parts = fullName.split(/\s+/);
  return parts.map(capitalizeFirstLetter).join(" ");
}

/**
 * Capitalize a full name, returning `undefined` when it is empty or
 * whitespace-only. Mirrors the trim + truthiness guard used by
 * `mapToParticipant`, so consumers can rely on `fullName` being either a
 * meaningful string or `undefined` (never `""`) and fall back to the phone
 * number with `??`.
 */
export function normalizeFullName(fullName: string): string | undefined {
  const trimmed = fullName.trim();
  return trimmed ? capitalizeString(trimmed) : undefined;
}

export function removeDoubleQuotes(value: string) {
  return value.replaceAll(/"/g, "");
}

export function surroundWithQuotes(value: string) {
  return `"${value}"`;
}
